import React, { useEffect, useRef, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { trackEvent } from "../analytics/ga";
import { useAuth } from "../auth/AuthContext";
import { SUPPORTED_LANGUAGES } from "../i18n";
import { useLanguage } from "../i18n/LanguageContext";
import "./authPages.css";

export const LoginPage = () => {
  const { user, loading, configured, signIn, signUp, resetPassword } =
    useAuth();
  const { language, setLanguage, portal } = useLanguage();
  const copy = portal.login;
  const navigate = useNavigate();
  const [mode, setMode] = useState("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [languageMenuOpen, setLanguageMenuOpen] = useState(false);
  const languageMenuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        languageMenuRef.current &&
        !languageMenuRef.current.contains(event.target)
      ) {
        setLanguageMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  if (!loading && user) {
    return <Navigate to="/app/snippets" replace />;
  }

  const currentLanguage =
    SUPPORTED_LANGUAGES.find(({ code }) => code === language) ??
    SUPPORTED_LANGUAGES[0];

  const switchMode = (nextMode) => {
    setMode(nextMode);
    setError("");
    setSuccess("");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setSuccess("");
    setSubmitting(true);

    try {
      if (mode === "forgot") {
        await resetPassword(email.trim());
        trackEvent({ category: "Auth", action: "ForgotPassword" });
        setSuccess(copy.resetEmailSent);
        return;
      }

      if (mode === "signin") {
        await signIn(email.trim(), password);
        trackEvent({ category: "Auth", action: "SignIn" });
      } else {
        await signUp(email.trim(), password);
        trackEvent({ category: "Auth", action: "SignUp" });
      }
      navigate("/app/snippets");
    } catch (err) {
      setError(
        err?.message ||
          (mode === "forgot" ? copy.resetFailed : copy.authFailed)
      );
    } finally {
      setSubmitting(false);
    }
  };

  const title =
    mode === "forgot"
      ? copy.forgotPasswordTitle
      : mode === "signin"
        ? copy.signIn
        : copy.createAccount;

  const submitLabel =
    mode === "forgot"
      ? copy.sendResetLink
      : mode === "signin"
        ? copy.signIn
        : copy.createAccount;

  return (
    <div className="auth-page">
      <div className="auth-card">
        <div className="auth-card-top">
          <Link to="/" className="auth-brand">
            FastLex
          </Link>
          <div className="language-dropdown" ref={languageMenuRef}>
            <button
              type="button"
              className="language-dropdown-toggle"
              onClick={() => setLanguageMenuOpen((open) => !open)}
              aria-expanded={languageMenuOpen}
              aria-haspopup="listbox"
              aria-label={portal.common.selectLanguage}
            >
              <span className="language-dropdown-label">
                {currentLanguage.label}
              </span>
              <span className="language-dropdown-chevron" aria-hidden="true">
                ▾
              </span>
            </button>
            {languageMenuOpen && (
              <ul
                className="language-dropdown-menu auth-language-menu"
                role="listbox"
              >
                {SUPPORTED_LANGUAGES.map(({ code, name }) => (
                  <li key={code} role="presentation">
                    <button
                      type="button"
                      role="option"
                      aria-selected={language === code}
                      className={`language-dropdown-item ${
                        language === code ? "active" : ""
                      }`}
                      onClick={() => {
                        setLanguage(code);
                        setLanguageMenuOpen(false);
                      }}
                    >
                      {name}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
        <h1>{title}</h1>
        {mode === "forgot" && (
          <p className="auth-subtitle">{copy.forgotPasswordSubtitle}</p>
        )}

        {!configured && (
          <div className="auth-error">{copy.firebaseNotConfigured}</div>
        )}

        <form onSubmit={handleSubmit} className="auth-form">
          <label htmlFor="email">{copy.email}</label>
          <input
            id="email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            disabled={!configured || submitting}
          />

          {mode !== "forgot" && (
            <>
              <div className="auth-password-row">
                <label htmlFor="password">{copy.password}</label>
                {mode === "signin" && (
                  <button
                    type="button"
                    className="auth-forgot-link"
                    onClick={() => switchMode("forgot")}
                    disabled={!configured || submitting}
                  >
                    {copy.forgotPassword}
                  </button>
                )}
              </div>
              <input
                id="password"
                type="password"
                autoComplete={
                  mode === "signin" ? "current-password" : "new-password"
                }
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                disabled={!configured || submitting}
              />
            </>
          )}

          {error && <div className="auth-error">{error}</div>}
          {success && <div className="auth-success">{success}</div>}

          <button
            type="submit"
            className="auth-submit"
            disabled={!configured || submitting}
          >
            {submitting ? copy.pleaseWait : submitLabel}
          </button>
        </form>

        <button
          type="button"
          className="auth-switch"
          onClick={() =>
            switchMode(mode === "signin" ? "signup" : "signin")
          }
        >
          {mode === "forgot"
            ? copy.backToSignIn
            : mode === "signin"
              ? copy.needAccount
              : copy.haveAccount}
        </button>
      </div>
    </div>
  );
};
